import { AuthRepository } from '../../repositories/auth/AuthRepository'
import { AuthApiDataHandler } from '../../data-handlers/auth/AuthApiDataHandler'
import AuthStorage from '../storage/auth-storage/AuthStorage'
import SessionStorage from '../storage/session-storage/SessionStorage'
import { setCrashlyticsUserId } from '../crashlytics/crashlyticsService'

// Separate AuthRepository instance from the one in epics/auth/index.ts — safe because
// AuthRepository/AuthApiDataHandler are stateless delegates that both ultimately mutate
// the one shared `api` singleton's headers, not any per-instance state. This module
// exists so UI code (the organization picker, the drawer's "change organization" flow)
// can reuse logout's cleanup steps without importing the epics module, which owns the
// OTA subsystem and would make a UI -> epics dependency a layering violation waiting to
// become an import cycle.
//
// Constructed lazily, not at module scope: this module is reached from epics/auth/index.ts
// (via the import below), which is itself reached from the api singleton's own import of
// `store` -> epics -> here -> AuthApiDataHandler -> api again. Eagerly instantiating
// AuthApiDataHandler here (which reads the `api` binding immediately) hit that cycle at a
// point where `api` was still in its temporal dead zone, crashing every cold start with
// "Cannot access 'api' before initialization" — reproduced on-device before this fix.
// Deferring the read until the function is actually called sidesteps the cycle entirely.
let authRepository: AuthRepository | undefined

function getAuthRepository(): AuthRepository {
  if (!authRepository) {
    authRepository = new AuthRepository(new AuthApiDataHandler())
  }
  return authRepository
}

// Exactly what logoutEpic does today, minus the OTA promoteStagedBundle call (that
// stays epic-only — see logoutEpic in services/store/epics/auth/index.ts). Missing the
// AuthStorage.remove step here would let a reload restore the old session against
// whatever the new organization/backend is — the same race M1 fixed, in a new place.
export async function performLogoutCleanup(): Promise<void> {
  getAuthRepository().setAuth('')
  await AuthStorage.remove('basic.auth')
  SessionStorage.remove('webPush')
  void setCrashlyticsUserId('')
}
